import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ShellConfigService } from 'continity-shared-shell';

import { PRIVACY_CONSENT_VERSION } from './privacy-consent.version';

/** Must match the backend `PrivacyConsentStatusDto` (Compliance slice). */
export interface PrivacyConsentStatusDto {
  tenantId: string;
  consentKey: string;
  lastAcceptedVersion: string | null;
  givenAtUtc: string | null;
  givenByUserId: string | null;
}

/**
 * HTTP seam for the tenant privacy/legal consent in the Phoenix portal.
 *
 * ARCHITECTURE DECISION — phoenix-LOCAL mirror, not a shared library:
 * the consent is shared between the portals at the BACKEND, not the frontend. The record is
 * TENANT-wide (`TenantPrivacyConsent`, one row per tenant with ConsentKey "privacy", tenant
 * derived server-side from the CompanyId claim), and BOTH portals call the same
 * `/api/privacy-consent` endpoints with the same bearer token — accepting in either portal
 * satisfies the other. That means the API contract IS the sharing seam; a frontend library
 * would share ~30 lines of HTTP glue while dragging the main portal's consent DIALOG along,
 * which is welded to main-portal-only infra (SupportRequestDialog, portal Theme/AuthService,
 * release-notes chaining) — extracting that would be a behaviour-relevant refactor of the
 * main portal for no product gain. The three things that MUST NOT drift are pinned elsewhere:
 * the legal TEXTS (shared i18n bundles via asset glob + i18n-parity.spec), the VERSION
 * constant (consent-version-parity.spec) and the DTO/endpoint (backend-owned).
 *
 * Accepting requires the BCMEditor or CompanyAdmin role server-side (`PrivacyConsentService
 * .EnsureCanAcceptConsent`); reading the status is open to every authenticated tenant user.
 */
@Injectable({ providedIn: 'root' })
export class PhoenixPrivacyConsentService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ShellConfigService);

  private get baseUrl(): string {
    return `${this.config.apiUrl}/privacy-consent`;
  }

  getStatus(): Observable<PrivacyConsentStatusDto> {
    return this.http.get<PrivacyConsentStatusDto>(`${this.baseUrl}/status`);
  }

  accept(version: string): Observable<PrivacyConsentStatusDto> {
    return this.http.post<PrivacyConsentStatusDto>(`${this.baseUrl}/accept`, { version });
  }

  /** Same rule as the main portal's MainLayout gate: never accepted, or an older version. */
  needsConsent(status: PrivacyConsentStatusDto): boolean {
    return !status.lastAcceptedVersion || status.lastAcceptedVersion !== PRIVACY_CONSENT_VERSION;
  }
}
